/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useEffect, useState } from 'react';
import styles from './partners.module.scss';
import { ObraSocios } from '../../../types/ObraDetails';
import partnerPicture from '../../../assets/client.svg';
import { useTranslation } from 'react-i18next';
import InformationIcon from '../components/informationIcon/informationIcon';
import PartnerBox from './components/PartnerBox/PartnerBox';
import ObrasAPI from '../../../services/APIObras';
import ErrorDetails from '../components/ErrorDetails/ErrorDetails';
import { PartnersLoadingSkeleton } from './components/partnersLoadingSkeleton/partnersLoadingSkeleton';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PartnersProps {
  codCentro: string;
  projectId?: string;
  projectIdLoading: boolean;
  projectIdError: boolean;
}

const Partners: FC<PartnersProps> = (props) => {
  const { codCentro, projectId, projectIdLoading, projectIdError } = props;
  const [t] = useTranslation();
  const [partners, setPartners] = useState<ObraSocios>();
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const api = ObrasAPI.getInstance();
    setPartners(undefined);
    setLoadError(false);
    if (!projectId) return;

    api.getObraPartnersAPI(codCentro, projectId)
      .then((partnersResponse) => {
        setPartners(partnersResponse);
      })
      .catch(() => {
        setLoadError(true);
      });
  }, [codCentro, projectId]);

  if (loadError || projectIdError) return <ErrorDetails />;
  if (!partners || projectIdLoading) return <PartnersLoadingSkeleton />;

  const calculaParticipacion = (participacion: number) => {
    const [wholeNumber, decimal] = participacion.toString().split('.');
    return `${wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}${decimal && decimal.substring(0, 2) !== '00' ? ',' + decimal.substring(0, 2) : ''}%`;
  };

  const originalCompaniesData = partners.empresas || [];
  const updatedCompaniesData = partners.empresasActualizadas || [];
  const hasUpdatedCompanies = updatedCompaniesData.length > 0;
  const hasOriginalCompanies = originalCompaniesData.length > 0;
  const currentCompaniesData = hasUpdatedCompanies ? updatedCompaniesData : originalCompaniesData;
  const sameParticipacion =
    originalCompaniesData.length === updatedCompaniesData.length &&
    originalCompaniesData.every(emp =>
      updatedCompaniesData.some(e => e.participacion === emp.participacion)
    );

  // Sort the partners by their participation from highest to lowest, giving priority to "Acciona Construcción".
  const partnersSorted = currentCompaniesData?.filter(e => Number(e.participacion) > 0)
    .sort((a, b) => {
      if (b.participacion !== a.participacion) {
        return b.participacion - a.participacion;
      } else {
        const aIsAccionaConstruccion = a.nombre.includes('ACCIONA Construcción');
        const bIsAccionaConstruccion = b.nombre.includes('ACCIONA Construcción');

        if (aIsAccionaConstruccion && !bIsAccionaConstruccion) {
          return -1;
        } else if (!aIsAccionaConstruccion && bIsAccionaConstruccion) {
          return 1;
        } else {
          return 0;
        }
      }
    });

  // Sort the partners by their participation from highest to lowest, giving priority to "Acciona Construcción".
  const partnersSortedFormer = originalCompaniesData
    ?.filter(e => Number(e.participacion) > 0)
    .sort((a, b) => {
      if (b.participacion !== a.participacion) {
        return b.participacion - a.participacion;
      } else {
        const aIsAccionaConstruccion = a.nombre.includes('ACCIONA Construcción');
        const bIsAccionaConstruccion = b.nombre.includes('ACCIONA Construcción');

        if (aIsAccionaConstruccion && !bIsAccionaConstruccion) {
          return -1;
        } else if (!aIsAccionaConstruccion && bIsAccionaConstruccion) {
          return 1;
        } else {
          return 0;
        }
      }
    });

  const addMonth = (fechaStr: string | undefined): string | null => {
    if (!fechaStr) return null;

    const parts = fechaStr.split('/');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month, day);
    date.setMonth(date.getMonth() + 1);

    const newDay = String(date.getDate()).padStart(2, '0');
    const newMonth = String(date.getMonth() + 1).padStart(2, '0');
    const newYear = date.getFullYear();

    return `${newDay}/${newMonth}/${newYear}`;
  };

  const finalDate = partners.fechaParticipacionActualizacion
  ?? addMonth(partners.fechaParticipacionAntigua);

  // Casuística 1: partnersSorted vacío y partnersSortedFormer tiene valores
  const showFormerAsCurrent = (!partnersSorted || partnersSorted.length === 0) &&
                              partnersSortedFormer &&
                              partnersSortedFormer.length > 0;

  // Casuística 3: partnersSorted y partnersSortedFormer vacíos
  const bothEmpty = (!partnersSorted || partnersSorted.length === 0) &&
                   (!partnersSortedFormer || partnersSortedFormer.length === 0);
  const showPreviousPartners = hasUpdatedCompanies && hasOriginalCompanies && !sameParticipacion && !showFormerAsCurrent &&
    partnersSorted && partnersSorted.length > 0;


  // ACPQ1-1204 - Function to check if there are UTE partners
  const hasUtePartners = () => {

    if (bothEmpty)
      return false;

    const partners = [...partnersSorted, ...partnersSortedFormer];
    return partners.some(partner =>
      !partner.nombre?.toLowerCase().includes('acciona construcción')
    );

  };

  return (
    <div data-testid='partners-test' className={styles.partnersContainer}>
      <div className={styles.partnerWrapper}>
        <div id='partners' className={styles.heading}>{t('details.partners.tab')}
          <InformationIcon message={t('help.partners.partners')} />
        </div>
        {hasUtePartners() && (
          <div className={styles.lastUpdate} id='lastUpdate'>{t('details.partners.lastUpdate')}:
            <span className={styles.lastUpdateDate} id='lastUpdateDate'> {finalDate ?? t('details.notInformed')}</span>
          </div>
        )}
      </div>


      {showFormerAsCurrent ? (
        <>
          <div id='partners' className={styles.subtitle}>{t('advancedFilters.now')}</div>
          <div className={styles.boxes}>
            {partnersSortedFormer.map((e) => (
              <div className={styles.boxContainer} key={e.id}>
                <PartnerBox
                  picture={partnerPicture}
                  item={{ title: e.id, value: e.nombre }}
                  participationItem={{ title: t('details.partners.participation'), value: calculaParticipacion(e.participacion) }}
                />
              </div>
            ))}
          </div>
        </>
      ) : bothEmpty ? (
        <div className={styles.boxContainer}>
          <PartnerBox picture={partnerPicture} item={{ title: t('details.partners.notApplied') ?? '', value: t('details.partners.notApplied') ?? '' }} />
        </div>
      ) : (
        <>
          <div id='partners' className={styles.subtitle}>{t('advancedFilters.now')}</div>
          {partnersSorted && partnersSorted.length > 0 ? (
            <div className={styles.boxes}>
              {partnersSorted.map((e) => (
                <div className={styles.boxContainer} key={e.id}>
                  <PartnerBox
                    picture={partnerPicture}
                    item={{ title: e.id, value: e.nombre }}
                    participationItem={{ title: t('details.partners.participation'), value: calculaParticipacion(e.participacion) }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.boxContainer}>
              <PartnerBox picture={partnerPicture} item={{ title: t('details.partners.notApplied') ?? '', value: t('details.partners.notApplied') ?? ''  }} />
            </div>
          )}
        </>
      )}

      {showPreviousPartners && (
        <>
          <div id='partners' className={styles.subtitle}>{t('advancedFilters.before')}</div>
          {partnersSortedFormer && partnersSortedFormer.length > 0 ? (
            <div className={styles.boxes}>
              {partnersSortedFormer.map((e) => (
                <div className={styles.boxContainer} key={e.id}>
                  <PartnerBox
                    picture={partnerPicture}
                    item={{ title: e.id, value: e.nombre }}
                    participationItem={{ title: t('details.partners.participation'), value: calculaParticipacion(e.participacion) }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.boxContainer}>
              <PartnerBox picture={partnerPicture} item={{ title: t('details.partners.notApplied') ?? '', value: t('details.partners.notApplied') ?? ''  }} />
            </div>
          )}
        </>
      )}

      <div className='footer' style={{ marginBottom: '75px' }}></div>
    </div>
  );
};

export default Partners;
